<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-danger text-white">
          <h5 class="modal-title">
            <i class="bx bx-error-circle me-2"></i>Declare Disease Outbreak
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Alert Notice -->
          <div class="alert alert-danger d-flex align-items-center mb-4" role="alert">
            <i class="bx bx-error-circle fs-4 me-3"></i>
            <div>
              <strong>Important:</strong> This action will immediately notify Woreda and Zonal health offices.
              Please ensure all information is accurate before submission.
            </div>
          </div>

          <!-- Step 1: Disease Information -->
          <div v-if="step === 1">
            <h6 class="mb-3">Disease Information</h6>

            <!-- Disease Type -->
            <div class="mb-3">
              <label class="form-label">Disease Type <span class="text-danger">*</span></label>
              <select v-model="outbreakForm.diseaseType" class="form-select" required>
                <option value="">Select disease</option>
                <optgroup label="Immediately Notifiable">
                  <option value="AFP">Acute Flaccid Paralysis (AFP)</option>
                  <option value="Cholera">Cholera</option>
                  <option value="Measles">Measles</option>
                  <option value="Meningitis">Meningitis</option>
                  <option value="Yellow_Fever">Yellow Fever</option>
                  <option value="COVID-19">COVID-19</option>
                  <option value="Viral_Hemorrhagic_Fever">Viral Hemorrhagic Fever</option>
                </optgroup>
                <optgroup label="Weekly Reportable">
                  <option value="Malaria">Malaria</option>
                  <option value="Typhoid">Typhoid Fever</option>
                  <option value="Dysentery">Dysentery</option>
                  <option value="Diarrhea_Blood">Diarrhea with Blood</option>
                  <option value="Diarrhea_Dehydration">Diarrhea with Dehydration</option>
                </optgroup>
                <optgroup label="Other">
                  <option value="Other">Other (Specify)</option>
                </optgroup>
              </select>
            </div>

            <!-- Other Disease Specification -->
            <div v-if="outbreakForm.diseaseType === 'Other'" class="mb-3">
              <label class="form-label">Specify Disease <span class="text-danger">*</span></label>
              <input v-model="outbreakForm.otherDisease" type="text" class="form-control"
                     placeholder="Enter disease name" required>
            </div>

            <!-- First Case Date -->
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Date of First Case <span class="text-danger">*</span></label>
                <input v-model="outbreakForm.firstCaseDate" type="date" class="form-control"
                       :max="today" required>
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">Date of Peak Cases</label>
                <input v-model="outbreakForm.peakCaseDate" type="date" class="form-control"
                       :min="outbreakForm.firstCaseDate" :max="today">
              </div>
            </div>

            <!-- Case Numbers -->
            <div class="row">
              <div class="col-md-4 mb-3">
                <label class="form-label">Total Cases <span class="text-danger">*</span></label>
                <input v-model.number="outbreakForm.totalCases" type="number" class="form-control"
                       min="1" required>
              </div>

              <div class="col-md-4 mb-3">
                <label class="form-label">Active Cases <span class="text-danger">*</span></label>
                <input v-model.number="outbreakForm.activeCases" type="number" class="form-control"
                       min="0" :max="outbreakForm.totalCases" required>
              </div>

              <div class="col-md-4 mb-3">
                <label class="form-label">Deaths</label>
                <input v-model.number="outbreakForm.deaths" type="number" class="form-control"
                       min="0" :max="outbreakForm.totalCases">
              </div>
            </div>

            <!-- Age Group Distribution -->
            <div class="mb-3">
              <label class="form-label">Age Group Most Affected</label>
              <select v-model="outbreakForm.ageGroupMostAffected" class="form-select">
                <option value="">Select age group</option>
                <option value="0-5">Under 5 years</option>
                <option value="5-14">5-14 years</option>
                <option value="15-49">15-49 years</option>
                <option value="50+">50+ years</option>
                <option value="all">All age groups equally</option>
              </select>
            </div>
          </div>

          <!-- Step 2: Geographic Distribution -->
          <div v-else-if="step === 2">
            <h6 class="mb-3">Geographic Distribution</h6>

            <!-- Affected Areas -->
            <div class="mb-3">
              <label class="form-label">Affected Villages/Areas <span class="text-danger">*</span></label>
              <div class="input-group mb-2">
                <input v-model="newArea" type="text" class="form-control"
                       placeholder="Enter village/area name" @keyup.enter="addArea">
                <button class="btn btn-primary" type="button" @click="addArea">
                  <i class="bx bx-plus"></i> Add
                </button>
              </div>

              <!-- Added Areas -->
              <div v-if="outbreakForm.affectedAreas.length > 0" class="mt-2">
                <div v-for="(area, index) in outbreakForm.affectedAreas" :key="index"
                     class="badge bg-primary me-2 mb-2">
                  {{ area }}
                  <i class="bx bx-x cursor-pointer ms-1" @click="removeArea(index)"></i>
                </div>
              </div>
              <small class="text-muted">Add all affected villages/areas in your kebele</small>
            </div>

            <!-- Affected Households -->
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Number of Affected Households <span class="text-danger">*</span></label>
                <input v-model.number="outbreakForm.affectedHouseholds" type="number"
                       class="form-control" min="1" required>
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">Estimated Population at Risk</label>
                <input v-model.number="outbreakForm.populationAtRisk" type="number"
                       class="form-control" min="1">
              </div>
            </div>

            <!-- Transmission Pattern -->
            <div class="mb-3">
              <label class="form-label">Transmission Pattern</label>
              <select v-model="outbreakForm.transmissionPattern" class="form-select">
                <option value="">Select pattern</option>
                <option value="household">Within households</option>
                <option value="community">Community-wide</option>
                <option value="institutional">Institutional (school, workplace)</option>
                <option value="waterborne">Water source related</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>

            <!-- Source -->
            <div class="mb-3">
              <label class="form-label">Suspected Source/Cause</label>
              <textarea v-model="outbreakForm.suspectedSource" class="form-control" rows="2"
                        placeholder="E.g., contaminated water source, food, contact with infected person..."></textarea>
            </div>
          </div>

          <!-- Step 3: Response Actions -->
          <div v-else-if="step === 3">
            <h6 class="mb-3">Response Actions & Additional Information</h6>

            <!-- Immediate Actions Taken -->
            <div class="mb-3">
              <label class="form-label">Immediate Actions Taken <span class="text-danger">*</span></label>
              <div class="form-check mb-2">
                <input v-model="outbreakForm.actionsTaken" class="form-check-input" type="checkbox"
                       value="isolation" id="action_isolation">
                <label class="form-check-label" for="action_isolation">
                  Patient isolation/quarantine implemented
                </label>
              </div>
              <div class="form-check mb-2">
                <input v-model="outbreakForm.actionsTaken" class="form-check-input" type="checkbox"
                       value="health_education" id="action_education">
                <label class="form-check-label" for="action_education">
                  Health education provided to community
                </label>
              </div>
              <div class="form-check mb-2">
                <input v-model="outbreakForm.actionsTaken" class="form-check-input" type="checkbox"
                       value="contact_tracing" id="action_tracing">
                <label class="form-check-label" for="action_tracing">
                  Contact tracing initiated
                </label>
              </div>
              <div class="form-check mb-2">
                <input v-model="outbreakForm.actionsTaken" class="form-check-input" type="checkbox"
                       value="sample_collection" id="action_samples">
                <label class="form-check-label" for="action_samples">
                  Samples collected for laboratory confirmation
                </label>
              </div>
              <div class="form-check mb-2">
                <input v-model="outbreakForm.actionsTaken" class="form-check-input" type="checkbox"
                       value="treatment" id="action_treatment">
                <label class="form-check-label" for="action_treatment">
                  Treatment provided to affected cases
                </label>
              </div>
            </div>

            <!-- Resources Needed -->
            <div class="mb-3">
              <label class="form-label">Urgent Resources Needed</label>
              <div class="form-check mb-2">
                <input v-model="outbreakForm.resourcesNeeded" class="form-check-input" type="checkbox"
                       value="medical_supplies" id="resource_medical">
                <label class="form-check-label" for="resource_medical">
                  Medical supplies (specify in notes)
                </label>
              </div>
              <div class="form-check mb-2">
                <input v-model="outbreakForm.resourcesNeeded" class="form-check-input" type="checkbox"
                       value="personnel" id="resource_personnel">
                <label class="form-check-label" for="resource_personnel">
                  Additional health personnel
                </label>
              </div>
              <div class="form-check mb-2">
                <input v-model="outbreakForm.resourcesNeeded" class="form-check-input" type="checkbox"
                       value="laboratory" id="resource_lab">
                <label class="form-check-label" for="resource_lab">
                  Laboratory support
                </label>
              </div>
              <div class="form-check mb-2">
                <input v-model="outbreakForm.resourcesNeeded" class="form-check-input" type="checkbox"
                       value="transportation" id="resource_transport">
                <label class="form-check-label" for="resource_transport">
                  Transportation for referrals
                </label>
              </div>
            </div>

            <!-- Additional Notes -->
            <div class="mb-3">
              <label class="form-label">Additional Information</label>
              <textarea v-model="outbreakForm.additionalNotes" class="form-control" rows="3"
                        placeholder="Any other relevant information about the outbreak..."></textarea>
            </div>

            <!-- Contact Information -->
            <div class="alert alert-info">
              <h6 class="alert-heading">Your Contact Information</h6>
              <p class="mb-2"><strong>Name:</strong> {{ userInfo.name }}</p>
              <p class="mb-2"><strong>Phone:</strong> {{ userInfo.phone }}</p>
              <p class="mb-0"><strong>Health Post:</strong> {{ userInfo.healthPost }}</p>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>

          <button v-if="step === 1" type="button" class="btn btn-primary"
                  @click="validateStep1" :disabled="!isStep1Valid">
            Next <i class="bx bx-chevron-right"></i>
          </button>

          <button v-else-if="step === 2" type="button" class="btn btn-secondary me-2" @click="previousStep">
            <i class="bx bx-chevron-left"></i> Back
          </button>
          <button v-if="step === 2" type="button" class="btn btn-primary"
                  @click="validateStep2" :disabled="!isStep2Valid">
            Next <i class="bx bx-chevron-right"></i>
          </button>

          <button v-else-if="step === 3" type="button" class="btn btn-secondary me-2" @click="previousStep">
            <i class="bx bx-chevron-left"></i> Back
          </button>
          <button v-if="step === 3" type="button" class="btn btn-danger"
                  @click="confirmDeclaration" :disabled="!isStep3Valid || submitting">
            <span v-if="submitting">
              <span class="spinner-border spinner-border-sm me-1"></span> Declaring...
            </span>
            <span v-else>
              <i class="bx bx-error-circle"></i> Declare Outbreak
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Confirmation Dialog -->
  <div v-if="showConfirmation" class="modal fade show d-block" tabindex="-1"
       style="background-color: rgba(0,0,0,0.5); z-index: 1060;">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-danger text-white">
          <h5 class="modal-title">Confirm Outbreak Declaration</h5>
        </div>
        <div class="modal-body">
          <div class="text-center mb-4">
            <i class="bx bx-error-circle text-danger" style="font-size: 64px;"></i>
          </div>
          <p class="text-center">
            You are about to declare a <strong>{{ outbreakForm.diseaseType }}</strong> outbreak
            affecting <strong>{{ outbreakForm.totalCases }}</strong> cases.
          </p>
          <p class="text-center text-danger">
            This will immediately notify all relevant health authorities. This action cannot be undone.
          </p>
          <p class="text-center">
            <strong>Are you sure you want to proceed?</strong>
          </p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showConfirmation = false">
            Cancel
          </button>
          <button type="button" class="btn btn-danger" @click="declareOutbreak">
            Yes, Declare Outbreak
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "DeclareOutbreakModal",
  data() {
    return {
      step: 1,
      loading: false,
      submitting: false,
      showConfirmation: false,

      outbreakForm: {
        diseaseType: '',
        otherDisease: '',
        firstCaseDate: '',
        peakCaseDate: '',
        totalCases: null,
        activeCases: null,
        deaths: 0,
        ageGroupMostAffected: '',
        affectedAreas: [],
        affectedHouseholds: null,
        populationAtRisk: null,
        transmissionPattern: '',
        suspectedSource: '',
        actionsTaken: [],
        resourcesNeeded: [],
        additionalNotes: ''
      },

      newArea: '',
      today: new Date().toISOString().split('T')[0],

      userInfo: {
        name: sessionStorage.getItem('userName') || 'HEW Officer',
        phone: sessionStorage.getItem('userPhone') || '',
        healthPost: sessionStorage.getItem('kebeleName') || ''
      }
    };
  },

  computed: {
    isStep1Valid() {
      return this.outbreakForm.diseaseType &&
          (this.outbreakForm.diseaseType !== 'Other' || this.outbreakForm.otherDisease) &&
          this.outbreakForm.firstCaseDate &&
          this.outbreakForm.totalCases > 0 &&
          this.outbreakForm.activeCases >= 0 &&
          this.outbreakForm.activeCases <= this.outbreakForm.totalCases;
    },

    isStep2Valid() {
      return this.outbreakForm.affectedAreas.length > 0 &&
          this.outbreakForm.affectedHouseholds > 0;
    },

    isStep3Valid() {
      return this.outbreakForm.actionsTaken.length > 0;
    }
  },

  methods: {
    validateStep1() {
      if (this.isStep1Valid) {
        this.step = 2;
      }
    },

    validateStep2() {
      if (this.isStep2Valid) {
        this.step = 3;
      }
    },

    previousStep() {
      this.step--;
    },

    addArea() {
      if (this.newArea.trim() && !this.outbreakForm.affectedAreas.includes(this.newArea.trim())) {
        this.outbreakForm.affectedAreas.push(this.newArea.trim());
        this.newArea = '';
      }
    },

    removeArea(index) {
      this.outbreakForm.affectedAreas.splice(index, 1);
    },

    confirmDeclaration() {
      if (this.isStep3Valid) {
        this.showConfirmation = true;
      }
    },

    async declareOutbreak() {
      this.showConfirmation = false;
      this.submitting = true;

      const data = {
        "function": "declareOutbreak",
        "diseaseType": this.outbreakForm.diseaseType === 'Other' ?
            this.outbreakForm.otherDisease : this.outbreakForm.diseaseType,
        "firstCaseDate": this.outbreakForm.firstCaseDate,
        "peakCaseDate": this.outbreakForm.peakCaseDate,
        "totalCases": this.outbreakForm.totalCases,
        "activeCases": this.outbreakForm.activeCases,
        "deaths": this.outbreakForm.deaths,
        "ageGroupMostAffected": this.outbreakForm.ageGroupMostAffected,
        "affectedAreas": this.outbreakForm.affectedAreas.join(', '),
        "affectedHouseholds": this.outbreakForm.affectedHouseholds,
        "populationAtRisk": this.outbreakForm.populationAtRisk,
        "transmissionPattern": this.outbreakForm.transmissionPattern,
        "suspectedSource": this.outbreakForm.suspectedSource,
        "actionsTaken": this.outbreakForm.actionsTaken.join(', '),
        "resourcesNeeded": this.outbreakForm.resourcesNeeded.join(', '),
        "additionalNotes": this.outbreakForm.additionalNotes
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.$emit('declared', {
            outbreakId: res.data.data.outbreakId,
            diseaseType: this.outbreakForm.diseaseType
          });
        } else {
          alert(res.data.message || 'Failed to declare outbreak');
        }
      } catch (e) {
        console.error("Failed to declare outbreak:", e);
        alert('Failed to declare outbreak. Please try again.');
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.cursor-pointer {
  cursor: pointer;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.form-check-input:checked {
  background-color: #dc3545;
  border-color: #dc3545;
}

.badge i {
  font-size: 12px;
}

.alert-danger {
  border-left: 4px solid #dc3545;
}

.btn-close-white {
  filter: invert(1);
}
</style>